import { Formik } from "formik";
import React from "react";
import { Config } from "../../../utils/config";
import fetchData from "../../../utils/api";
import { AccordionContent } from "../../../components/ui/accordion";
import { Label } from "../../../components/ui/label";
import FormError from "../../../components/FormError";
import DropDownList from "../../../components/DropDownList";
import { Button } from "../../../components/ui/button";
import { Icons } from "../../../components/icons";
import { Checkbox } from "../../../components/ui/checkbox";
import { Card } from "../../../components/ui/card";
import { PickUpMethodFormValidation } from "./Validation";
import { useTranslation } from "react-i18next";

export default function PickUpCart({
  cartList,
  authToken,
  id,
  restaurantDetail,
  handleCartList,
  handleAccordionClick,
}) {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [timeDropDownList, setTimeDropDownList] = React.useState<any>([]);
  const [dayDropDownList, setDayDropDownList] = React.useState<any>([]);
  const [selectedDayTime, setSelectedDayTime] = React.useState<any>([]);
  const [isRestaurantOpen, setIsRestaurantOpen] =
    React.useState<boolean>(false);

  const handlePickUpInfo = async () => {
    try {
      const res = await fetchData({
        url: "cart/pickup-info",
        method: "POST",
        token: authToken,
      });
      if (res.status_code === 200) {
        setDayDropDownList(res.data?.pickup_days);
        setTimeDropDownList(res.data?.pickup_time);
        setSelectedDayTime(
          res.data?.pickup_time[res.data?.pickup_days[0]?.label]
        );
        setIsRestaurantOpen(res.data.is_open);
      }
    } catch (error) {
      console.log("error", error);
    }
  };
  React.useEffect(() => {
    handlePickUpInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { t } = useTranslation();

  return (
    <div>
      <Formik
        enableReinitialize={true}
        initialValues={{
          method: cartList?.pickup?.method ?? "",
          date: cartList?.pickup?.date
            ? cartList?.pickup?.date
            : dayDropDownList?.length > 0
            ? dayDropDownList[0]?.value
            : "",
          time: cartList?.pickup?.time
            ? cartList?.pickup?.time
            : selectedDayTime?.length > 0
            ? selectedDayTime[0]?.value
            : "",
        }}
        validationSchema={PickUpMethodFormValidation}
        onSubmit={async (values) => {
          setIsLoading(true);
          const cachedCart = localStorage.getItem("cart");
          try {
            const body: any = {
              cart_id: cachedCart,
              method: values.method,
              date: values.date,
              time: values.time,
            };
            const res = await fetchData({
              url: "cart/pickup",
              method: "POST",
              body,
              token: authToken,
            });
            if (res.status_code === 200) {
              setIsLoading(false);
              handleCartList();
              handleAccordionClick("item-4");
            }
          } catch (error) {
            console.log("error", error);
          }
        }}
      >
        {({ errors, touched, setFieldValue, values, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <AccordionContent className="grid p-2 gap-2">
              <div className="grid gap-2 w-full">
                {Config.env.app.pickup_method.map((item, index) => {
                  if (!restaurantDetail?.services?.scheduled_orders) {
                    if (item.value === "later") {
                      return null;
                    }
                  }
                  if (item.value === "as_soon_as_possible") {
                    if (
                      restaurantDetail?.scheduledOrders?.other_asap === true
                    ) {
                      return null;
                    } else if (
                      restaurantDetail?.scheduledOrders?.other_asap === false &&
                      isRestaurantOpen
                    ) {
                      return null;
                    }
                  }

                  return (
                    <Card
                      id={item.label}
                      className="p-3 cursor-pointer"
                      key={index}
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox
                          disabled={isLoading}
                          id={item.value}
                          checked={values.method === item.value}
                          onCheckedChange={() => {
                            setFieldValue("method", item.value);
                          }}
                        />
                        <div className="flex justify-between w-full">
                          <div>
                            <label
                              htmlFor={item.value}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 self-center"
                            >
                              {t(item.label)}
                            </label>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
                {errors.method && touched.method ? (
                  <FormError msg={t(errors.method.toString())} />
                ) : null}
              </div>

              {values.method === "later" && (
                <div className="grid gap-2">
                  <div className="grid gap-2 w-full">
                    <Label className="text-left" htmlFor="date">
                      {t("cart.available_time_choice.form.date")}
                    </Label>
                    <div>
                      <DropDownList
                        id="date"
                        label={t("cart.available_time_choice.form.date")}
                        data={dayDropDownList}
                        value={values.date}
                        defaultValue={
                          dayDropDownList?.length > 0
                            ? dayDropDownList[0].value
                            : ""
                        }
                        onValueChange={(e: any) => {
                          setFieldValue("date", e, true);
                          setFieldValue("time", "", true);

                          const day: any = dayDropDownList.find(
                            (c) => c.value === e
                          )?.label;
                          setSelectedDayTime(timeDropDownList[day]);
                        }}
                        disabled={isLoading}
                      />

                      {errors.date && touched.date ? (
                        <FormError msg={t(errors.date as string)} />
                      ) : null}
                    </div>
                  </div>
                  <div className="grid gap-2 w-full">
                    <Label className="text-left" htmlFor="time">
                      {t("cart.available_time_choice.form.time")}
                    </Label>
                    <div>
                      <DropDownList
                        id="time"
                        label={t("cart.available_time_choice.form.time")}
                        placeholder={t(
                          "cart.available_time_choice.form.time_placeholder"
                        )}
                        data={selectedDayTime}
                        value={values.time}
                        onValueChange={(e: any) => {
                          setFieldValue("time", e, true);
                        }}
                        disabled={isLoading}
                        exceptTranslation={true}
                      />
                      {errors.time && touched.time ? (
                        <FormError msg={t(errors.time as string)} />
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex justify-end w-full">
                <Button disabled={isLoading} type="submit">
                  {isLoading && (
                    <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {t("save")}
                </Button>
              </div>
            </AccordionContent>
          </form>
        )}
      </Formik>
      {/* ) : (
        <AccordionContent className="grid p-2 gap-2">
          <Label>
            {t("cart.available_time_choice.form.as_soon_as_possible")}
          </Label>
        </AccordionContent>
      )} */}
    </div>
  );
}
